<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '../../lib/i18n';
  import { openaiModelsApi } from '../../lib/api';
  import { addNotification } from '../../stores/uiStore';
  import type { OpenAIModel } from '../../lib/types';
  
  export let modelId: number | null = null;
  export let show: boolean = false;
  
  const dispatch = createEventDispatcher();
  
  // Form state
  let name = '';
  let modelIdentifier = '';
  let description = '';
  let isActive = true;
  let loading = false;
  let error = '';
  
  // Watch for changes in modelId to load data when editing
  $: if (show && modelId !== null) {
    loadModel(modelId);
  } else if (show && modelId === null) {
    resetForm();
  }
  
  // Load model data for editing
  async function loadModel(id: number) {
    loading = true;
    error = '';
    
    try {
      const models = await openaiModelsApi.getAll(false);
      const model = models.find(m => m.id === id);
      
      if (model) {
        name = model.name || '';
        modelIdentifier = model.model_id || '';
        description = model.description || '';
        isActive = model.is_active || false;
      } else {
        error = t('Model not found');
      }
    } catch (err) {
      error = err.message || t('Error loading model');
    } finally {
      loading = false;
    }
  }
  
  // Reset form state
  function resetForm() {
    name = '';
    modelIdentifier = '';
    description = '';
    isActive = true;
    error = '';
  }
  
  // Handle form submission
  async function handleSubmit() {
    loading = true;
    error = '';
    
    try {
      const modelData: Partial<OpenAIModel> = {
        name,
        model_id: modelIdentifier,
        description,
        is_active: isActive
      };
      
      let result;
      if (modelId === null) {
        // Create new model
        result = await openaiModelsApi.create(modelData);
        addNotification('success', t('Model created successfully'));
      } else {
        // Update existing model
        result = await openaiModelsApi.update(modelId, modelData);
        addNotification('success', t('Model updated successfully'));
      }
      
      // Close the form and notify parent about the update
      closeForm();
      dispatch('refresh');
    } catch (err) {
      error = err.message || t('Error saving model');
    } finally {
      loading = false;
    }
  }
  
  // Close the form
  function closeForm() {
    show = false;
    resetForm();
    dispatch('close');
  }
</script>

{#if show}
  <div class="modal d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {modelId === null ? t('Add New OpenAI Model') : t('Edit OpenAI Model')}
          </h5>
          <button type="button" class="btn-close" on:click={closeForm}></button>
        </div>
        <div class="modal-body">
          {#if loading}
            <div class="d-flex justify-content-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{t('Loading...')}</span>
              </div>
            </div>
          {:else if error}
            <div class="alert alert-danger">
              {error}
            </div>
          {:else}
            <form on:submit|preventDefault={handleSubmit}>
              <div class="mb-3">
                <label for="name" class="form-label">{t('Name')}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="name" 
                  bind:value={name} 
                  placeholder="GPT-4 Turbo"
                  required
                />
                <div class="form-text">{t('Display name for the model')}</div>
              </div>
              
              <div class="mb-3">
                <label for="modelIdentifier" class="form-label">{t('Model ID')}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="modelIdentifier" 
                  bind:value={modelIdentifier} 
                  placeholder="gpt-4-turbo-preview"
                  required
                />
                <div class="form-text">{t('API identifier for the model')}</div>
              </div>
              
              <div class="mb-3">
                <label for="description" class="form-label">{t('Description')}</label>
                <textarea 
                  class="form-control" 
                  id="description" 
                  bind:value={description} 
                  rows="3"
                ></textarea>
              </div>
              
              <div class="form-check form-switch mb-3">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="isActive" 
                  bind:checked={isActive}
                />
                <label class="form-check-label" for="isActive">{t('Is Active')}</label>
              </div>
            </form>
          {/if}
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" on:click={closeForm}>{t('Cancel')}</button>
          <button 
            type="button" 
            class="btn btn-primary" 
            on:click={handleSubmit}
            disabled={loading}
          >
            {loading ? t('Saving...') : t('Save')}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}